import { wechatConfig } from '@/config/wechat';
import { createWechatPolling } from './wechatLoginPolling';

/**
 * 微信登录工具类 - 完整OAuth2.0流程
 * 1. 调用后端获取微信配置
 * 2. 使用微信官方WxLogin JS SDK
 * 3. 轮询登录状态
 */
export class WechatLogin {
  constructor() {
    this.wxLoginInstance = null;
    this.containerId = null;
    this.onLoginSuccess = null;
    this.onLoginFailed = null;
    this.onStatusChange = null;
    this.sessionKey = null;
    this.wechatConfig = null;
    this.polling = null;
    this.isSDKLoaded = false;
  }

  /**
   * 动态加载微信登录JS SDK
   * @returns {Promise} 加载结果
   */
  async loadWechatSDK() {
    if (this.isSDKLoaded || window.WxLogin) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = wechatConfig.jsSDK;
      script.async = true;
      
      script.onload = () => {
        this.isSDKLoaded = true;
        console.log('微信JS SDK加载成功');
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('微信登录SDK加载失败'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * 初始化微信登录二维码
   * @param {string} containerId 容器元素ID
   * @param {Object} options 配置选项
   * @returns {Promise} 初始化结果
   */
  async init(containerId, options = {}) {
    try {
      // 检查容器元素是否存在
      const container = document.getElementById(containerId);
      if (!container) {
        throw new Error(`未找到ID为 "${containerId}" 的容器元素`);
      }
      
      this.containerId = containerId;
      
      // 1. 调用后端初始化接口获取微信配置
          const { initWechatLogin } = await import('@/services/wechatService');
    const result = await initWechatLogin();
      
      if (!result.success || result.code !== 0) {
        throw new Error(result.message || '初始化微信登录失败');
      }
      
      // 保存后端返回的配置
      this.sessionKey = result.data.session_key;
      this.wechatConfig = {
        appid: result.data.appid,
        redirect_uri: result.data.redirect_uri,
        scope: result.data.scope,
        state: result.data.state
      };

      console.log('后端配置获取成功:', this.wechatConfig);

      // 2. 加载微信JS SDK
      await this.loadWechatSDK();

      // 3. 使用微信官方WxLogin生成二维码
      await this.createWxLogin(container, options);

      // 4. 初始化轮询
      this.polling = createWechatPolling();
      this.startPolling();

      // 触发状态回调
      if (this.onStatusChange) {
        this.onStatusChange('ready', { 
          sessionKey: this.sessionKey,
          containerId: containerId 
        });
      }

      return {
        success: true,
        sessionKey: this.sessionKey,
        containerId: containerId
      };

    } catch (error) {
      console.error('微信登录初始化失败:', error);
      if (this.onLoginFailed) {
        this.onLoginFailed(error.message || '初始化失败');
      }
      throw error;
    }
  }

  /**
   * 使用微信官方WxLogin创建二维码
   * @param {HTMLElement} container 容器元素
   * @param {Object} options 选项
   */
  async createWxLogin(container, options = {}) {
    if (!window.WxLogin) {
      throw new Error('微信JS SDK未加载');
    }

    if (!this.wechatConfig) {
      throw new Error('微信配置未获取');
    }

    // 清空容器
    container.innerHTML = '';

    // 使用环境变量配置的回调URL，如果没有配置则使用动态生成
    const envRedirectUri = process.env.VUE_APP_WECHAT_REDIRECT_URI;
    const frontendCallbackUrl = envRedirectUri || `${window.location.origin}/wechat-callback`;

    console.log('🔍 环境变量检查:');
    console.log('  VUE_APP_WECHAT_REDIRECT_URI =', envRedirectUri);
    console.log('  window.location.origin =', window.location.origin);
    console.log('  最终使用的redirect_uri =', frontendCallbackUrl);

    console.log('创建WxLogin实例，配置:', {
      ...this.wechatConfig,
      redirect_uri: frontendCallbackUrl
    });

    // 创建微信登录对象
    this.wxLoginInstance = new window.WxLogin({
      self_redirect: options.useNewWindow ? false : true, // 可配置：false=新窗口，true=iframe内跳转
      id: this.containerId,
      appid: this.wechatConfig.appid,
      scope: this.wechatConfig.scope,
      redirect_uri: frontendCallbackUrl, // 使用配置的回调URL
      state: this.wechatConfig.state, // 使用后端返回的原始state（包含session_key）
      style: options.style || 'black',
      color_scheme: options.color_scheme || 'dark',
      href: options.href || ''
    });

    console.log('WxLogin实例创建完成');
  }

  /**
   * 开始轮询登录状态
   */
  startPolling() {
    if (this.polling && this.sessionKey) {
      console.log('开始轮询登录状态:', this.sessionKey);
      
      this.polling.startPolling(this.sessionKey, {
        onSuccess: (userInfo) => {
          if (this.onStatusChange) {
            this.onStatusChange('success', userInfo);
          }
          if (this.onLoginSuccess) {
            this.onLoginSuccess(userInfo);
          }
        },
        onFailed: (error) => {
          if (this.onStatusChange) {
            this.onStatusChange('error', { message: error });
          }
          if (this.onLoginFailed) {
            this.onLoginFailed(error);
          }
        },
        onTimeout: (error) => {
          if (this.onStatusChange) {
            this.onStatusChange('timeout', { message: error });
          }
          if (this.onLoginFailed) {
            this.onLoginFailed(error);
          }
        }
      });
    }
  }

  /**
   * 停止轮询
   */
  stopPolling() {
    if (this.polling) {
      this.polling.stopPolling();
    }
  }

  /**
   * 刷新二维码
   */
  async refresh() {
    if (this.containerId) {
      console.log('刷新微信二维码');
      
      // 停止当前轮询
      this.stopPolling();
      
      // 重新初始化
      await this.init(this.containerId);
    }
  }

  /**
   * 销毁微信登录实例
   */
  destroy() {
    console.log('销毁微信登录实例');
    
    // 停止轮询
    this.stopPolling();
    
    if (this.containerId) {
      const container = document.getElementById(this.containerId);
      if (container) {
        container.innerHTML = '';
      }
    }
    
    this.wxLoginInstance = null;
    this.containerId = null;
    this.sessionKey = null;
    this.wechatConfig = null;
    this.polling = null;
    this.onLoginSuccess = null;
    this.onLoginFailed = null;
    this.onStatusChange = null;
  }

  /**
   * 设置登录成功回调
   */
  setOnLoginSuccess(callback) {
    this.onLoginSuccess = callback;
  }

  /**
   * 设置登录失败回调
   */
  setOnLoginFailed(callback) {
    this.onLoginFailed = callback;
  }

  /**
   * 设置状态变化回调
   */
  setOnStatusChange(callback) {
    this.onStatusChange = callback;
  }

  /**
   * 获取当前状态
   */
  getState() {
    return {
      sessionKey: this.sessionKey,
      containerId: this.containerId,
      isPolling: this.polling?.getPollingInfo()?.isPolling || false
    };
  }

  /**
   * 处理登录成功
   */
  handleLoginSuccess(userData) {
    console.log('微信登录成功:', userData);
    this.stopPolling();
    if (this.onLoginSuccess) {
      this.onLoginSuccess(userData);
    }
  }

  /**
   * 处理登录失败
   */
  handleLoginFailed(error) {
    console.error('微信登录失败:', error);
    this.stopPolling();
    if (this.onLoginFailed) {
      this.onLoginFailed(error);
    }
  }
}

/**
 * 创建微信登录实例
 */
export function createWechatLogin() {
  return new WechatLogin();
}

/**
 * 全局微信登录实例
 */
let globalWechatLogin = null;

/**
 * 获取全局微信登录实例
 */
export function getGlobalWechatLogin() {
  if (!globalWechatLogin) {
    globalWechatLogin = new WechatLogin();
  }
  return globalWechatLogin;
} 